import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Landmark, RefreshCw } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import { LoadingState } from '../../../components/AsyncStates';
import YearMonthPicker from '../../../components/yearMonthPicker';
import { useComplianceCountry } from '../../../hooks/useComplianceCountry';
import {
  PensionCoverageView,
  GpssaRemittanceView,
  MonthlyPensionContributionView,
  GpssaComplianceSummaryView,
  ComplianceDashboardView,
  GpssaContributionSkipView,
} from '../../../models/payroll';
import { getComplianceDashboard } from '../../../api/Payroll/compliance';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import {
  getGpssaCoverage,
  getGpssaRemittance,
  getContributions,
  getGpssaComplianceSummary,
} from '../../../api/Payroll/gpssa';
import { PensionTab } from '../ComplianceCenter/tabs/PensionTab';
import { ContributionSkipList } from '../ComplianceCenter/components/ContributionSkipList';

const PensionCenter: React.FC = () => {
  const { t } = useTranslation();
  const country = useComplianceCountry();

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [dashboard, setDashboard] = useState<ComplianceDashboardView | null>(null);
  const [coverage, setCoverage] = useState<PensionCoverageView[]>([]);
  const [remittance, setRemittance] = useState<GpssaRemittanceView | null>(null);
  const [contributions, setContributions] = useState<MonthlyPensionContributionView[]>([]);
  const [summary, setSummary] = useState<GpssaComplianceSummaryView | null>(null);
  // 🔴 CO-256 (Finding 2) — skip reasons live HERE, not in UaePensionPanel. Generation calls
  // onContributionsGenerated -> fetchData -> loading=true, which replaces the panel with
  // LoadingState and unmounted the panel that owned this state, so the "See warnings below" prose
  // pointed at nothing. Held by the page and rendered outside the loading branch, it survives.
  const [skipped, setSkipped] = useState<GpssaContributionSkipView[]>([]);

  /**
   * CO-261 — same staleness stamp as the Compliance Center, for the same reason. `fetchData` is
   * keyed on [year, month, t, country] and is ALSO called by the refresh button and by
   * `onContributionsGenerated`, so two runs can overlap; the reset above then means the loser's
   * coverage and remittance totals land under the winner's period heading with nothing to
   * contradict them. A counter, not a cleanup flag, because not every caller is an effect.
   */
  const requestSeqRef = useRef(0);

  const fetchData = useCallback(async () => {
    const requestSeq = ++requestSeqRef.current;
    const isStale = () => requestSeqRef.current !== requestSeq;
    setLoading(true);
    setError(null);
    // Same class as CO-256 on the Compliance Center: every panel below is written ONLY on success,
    // so a period whose calls fail (or a company that stops resolving as UAE) would keep rendering
    // the PREVIOUS period's coverage and remittance totals under the new period's heading.
    setDashboard(null);
    setCoverage([]);
    setRemittance(null);
    setContributions([]);
    setSummary(null);
    if (country !== 'UAE') {
      if (!isStale()) setLoading(false);
      return;
    }
    try {
      const [dashRes, coverageRes, remittanceRes, contribRes, summaryRes] = await Promise.all([
        getComplianceDashboard(year, month),
        getGpssaCoverage(year, month),
        getGpssaRemittance(year, month),
        getContributions(year, month),
        getGpssaComplianceSummary(year, month),
      ]);
      // A newer period (or refresh, or post-generation refetch) superseded this run — drop it
      // whole rather than writing one period's numbers under another's heading.
      if (isStale()) return;
      if (dashRes.errors.length === 0) setDashboard(dashRes.payload);
      if (coverageRes.errors.length === 0) setCoverage(coverageRes.payload);
      if (remittanceRes.errors.length === 0) setRemittance(remittanceRes.payload);
      if (contribRes.errors.length === 0) setContributions(contribRes.payload);
      if (summaryRes.errors.length === 0) setSummary(summaryRes.payload);
    } catch (err) {
      if (isStale()) return;
      setError(getApiErrorMessage(err, t, t('common.error')));
    } finally {
      // Only the run that still owns the screen may clear the loader; a stale one would
      // reveal a half-populated newer fetch.
      if (!isStale()) setLoading(false);
    }
  }, [year, month, t, country]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Skip reasons describe ONE generation of ONE period. Changing the period must clear them, or
  // last month's warnings would read as this month's.
  //
  // 🔴 CO-262 — `info` is the SAME fact in prose and must be reset with them. It was not, so
  // switching August → July left "Previous contributions for this period were replaced. 1
  // contribution(s) generated. 2 employee(s) were skipped. See warnings below." rendered under a
  // month with no contributions and no warnings — a success banner asserting a generation that
  // never happened for the period on screen, pointing at a skip list this effect had just emptied.
  // `error` needs no reset here because `fetchData` clears it on every run; `info` has no such
  // owner (it is set only by PensionTab's onInfo and cleared only by onError), which is exactly
  // why it survived the period change.
  useEffect(() => {
    setSkipped([]);
    setInfo(null);
  }, [year, month]);

  const title =
    country === 'KSA'
      ? t('payroll.pension.titleKsa', 'GOSI Contributions')
      : t('payroll.pension.titleUae', 'Pension — GPSSA & ADPF');

  return (
    <PageLayout title={title} icon={Landmark} accentColor="blue" maxWidth="1400px">
      <div className="space-y-6" data-testid="pension-center">
        <div className="flex items-center gap-4 flex-wrap">
          <YearMonthPicker
            date={new Date(year, month - 1, 1)}
            onChange={d => {
              setYear(d.getFullYear());
              setMonth(d.getMonth() + 1);
            }}
          />
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-input rounded-md hover:bg-muted transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            {t('common.refresh', 'Refresh')}
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}
        {info && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md text-emerald-700 text-sm">
            {info}
          </div>
        )}

        {/* Outside the loading branch on purpose — this is the "warnings below" the post-generation
            toast promises, and the generation itself triggers a re-fetch. */}
        <ContributionSkipList skipped={skipped} />

        {loading ? (
          <LoadingState title={t('common.loading')} size="md" />
        ) : (
          <PensionTab
            year={year}
            month={month}
            coverage={coverage}
            remittance={remittance}
            contributions={contributions}
            summary={summary}
            basicSalaryHealthy={dashboard?.basicSalaryInvariantHealthy}
            basicSalaryConfigCount={dashboard?.basicSalaryConfigCount}
            skipped={skipped}
            onSkipped={setSkipped}
            onContributionsGenerated={items => {
              setContributions(items);
              fetchData();
            }}
            onError={msg => {
              setInfo(null);
              setError(msg);
            }}
            onInfo={msg => {
              setError(null);
              setInfo(msg);
            }}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default PensionCenter;
